import s from '../styles/modules/SignUpSlot.module.scss';
import { Button } from './ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { Clock, User, Building2, UserCircle, Check, CalendarDays, Trash2 } from 'lucide-react';
import { Skeleton } from './ui/skeleton';
import { AlertCircle } from 'lucide-react';
import { useSignUpSlot } from '../hooks/useSignUpSlot';
import { SignUpSlotProps } from '../@types/props.types';

const SignUpSlot = ({ trainers, slot, onSuccessfulSignUp }: SignUpSlotProps) => {
  const {
    isTimeBooked,
    open,
    successDialogOpen,
    deleteDialog,
    errorDialog,
    errorMessage,
    cancelSuccessDialog,
    handleSignUp,
    handleSuccessClose,
    handleErrorClose,
    handleCancelClass,
    handleConfirmDelete,
    handleCancelSuccessClose,
    formatDate,
    userName,
    trainerListActiveState,
    setOpen,
    setDeleteDialog,
    setErrorDialog,
    setCancelSuccessDialog,
    isBookingAvailable,
  } = useSignUpSlot(slot, trainers, onSuccessfulSignUp);

  return (
    <div
      className={`${s.wrapper} ${
        isTimeBooked ? 'border-blue-200 bg-[#f9fafb] pb-2' : 'bg-[#f9fafb]'
      }`}>
      <div className={s.content}>
        <div className={s.timeBlock}>
          <Clock className={s.icon} />
          <span>{slot.date_time.slice(-5)}</span>
        </div>
        <div className={s.trainerBlock}>
          <User className={s.icon} />
          <div className="flex flex-row gap-2">
            {trainerListActiveState.map((trainer, index) => (
              <div key={index} className="whitespace-nowrap text-sm">
                {trainer.name}
              </div>
            ))}
          </div>
        </div>
      </div>
      {!isTimeBooked && isBookingAvailable && (
        <Button variant="default" size="sm" onClick={() => setOpen(true)} className={s.button}>
          Записаться
        </Button>
      )}
      {isTimeBooked && (
        <div className="mt-auto w-full">
          <div className="flex items-center justify-start border-blue-200 pt-2">
            <UserCircle className={s.icon} />
            <div className="ml-2 text-sm">{`${userName} (${slot.club_title})`}</div>
          </div>

          <Button
            variant="outline"
            size="sm"
            className="mt-4 h-10 w-full border-red-200 bg-white text-red-500 transition-colors hover:border-red-300 hover:bg-red-500 hover:text-red-100"
            onClick={handleCancelClass}>
            <Trash2 className="mr-2 h-4 w-4" />
            Отменить занятие
          </Button>
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-lg overflow-hidden p-0 sm:max-w-[425px]">
          <div className="bg-primary/10 p-6">
            <DialogHeader className="space-y-3">
              <DialogTitle className="text-center text-xl font-semibold">
                Запись на занятие
              </DialogTitle>
              <DialogDescription asChild>
                <div className="space-y-4 pt-3">
                  <div className="rounded-lg border bg-white p-4 shadow-sm">
                    <div className="space-y-3">
                      <div className="flex items-center gap-3 text-gray-600">
                        <Building2 className="h-5 w-5 text-primary" />
                        <span>АкваСтар: {localStorage.getItem('club_name')}</span>
                      </div>
                      <div className="flex items-center gap-3 text-gray-600">
                        <CalendarDays className="h-5 w-5 text-primary" />
                        <span>{formatDate(slot.date_time)}</span>
                      </div>
                      <div className="flex items-center gap-3 text-gray-600">
                        <User className="h-5 w-5 text-primary" />
                        <span>Инструктор: {trainerListActiveState[0]?.name || 'Не указан'}</span>
                      </div>
                      <div className="flex items-center gap-3 text-gray-600">
                        <UserCircle className="h-5 w-5 text-primary" />
                        <span>Ребенок: {userName}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </DialogDescription>
            </DialogHeader>
          </div>
          <div className="border-t bg-white p-6">
            <DialogFooter className="gap-3 sm:justify-end">
              <Button variant="outline" onClick={() => setOpen(false)}>
                Отмена
              </Button>
              <Button onClick={handleSignUp} size="lg">
                Записаться
              </Button>
            </DialogFooter>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog
        open={successDialogOpen}
        onOpenChange={(open) => {
          if (!open) {
            handleSuccessClose();
          }
        }}>
        <DialogContent className="rounded-lg overflow-hidden p-0 sm:max-w-[425px]">
          <div className="flex items-center justify-center bg-primary/10 p-6">
            <div className="flex h-16 w-16 items-center justify-center rounded-full bg-primary/20">
              <Check className="h-8 w-8 text-primary" />
            </div>
          </div>
          <div className="p-6">
            <DialogHeader>
              <DialogTitle className="mb-2 text-center text-xl">Запись подтверждена</DialogTitle>
              <DialogDescription className="text-center text-base">
                <div className="space-y-4">
                  <div className="flex items-center justify-center gap-2 text-gray-600">
                    <CalendarDays className="h-5 w-5" />
                    <span>{formatDate(slot.date_time)}</span>
                  </div>
                  <div className="flex items-center justify-center gap-2 text-gray-600">
                    <Building2 className="h-5 w-5" />
                    <span>АкваСтар: {localStorage.getItem('club_name')}</span>
                  </div>
                  <div className="flex items-center justify-center gap-2 text-gray-600">
                    <User className="h-5 w-5" />
                    <span>Инструктор: {trainerListActiveState[0]?.name || 'Не указан'}</span>
                  </div>
                  <div className="flex items-center justify-center gap-2 text-gray-600">
                    <UserCircle className="h-5 w-5" />
                    <span>{userName}</span>
                  </div>
                </div>
              </DialogDescription>
            </DialogHeader>
            <DialogFooter className="mt-8">
              <Button onClick={handleSuccessClose} className="w-full" size="lg">
                Готово
              </Button>
            </DialogFooter>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={deleteDialog} onOpenChange={setDeleteDialog}>
        <DialogContent className="rounded-lg sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle className="text-center text-xl">Подтверждение отмены</DialogTitle>
            <DialogDescription className="text-center">
              Вы уверены, что хотите отменить это занятие? Это действие нельзя будет отменить.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="flex gap-2 sm:gap-0">
            <Button variant="outline" className="flex-1" onClick={() => setDeleteDialog(false)}>
              Отмена
            </Button>
            <Button
              variant="destructive"
              className="flex-1 bg-red-500 hover:bg-red-600"
              onClick={handleConfirmDelete}>
              Подтвердить
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <Dialog open={errorDialog} onOpenChange={setErrorDialog}>
        <DialogContent className="overflow-hidden p-0 sm:max-w-[425px]">
          <div className="flex items-center justify-center bg-red-50 p-6">
            <div className="flex h-16 w-16 items-center justify-center rounded-full bg-red-100">
              <AlertCircle className="h-8 w-8 text-red-600" />
            </div>
          </div>
          <div className="p-6">
            <DialogHeader>
              <DialogTitle className="mb-2 text-center text-xl">Ошибка</DialogTitle>
              <DialogDescription className="text-center text-base text-gray-600">
                {errorMessage}
              </DialogDescription>
            </DialogHeader>
            <DialogFooter className="mt-6">
              <Button
                onClick={handleErrorClose}
                className="w-full bg-red-600 text-white hover:bg-red-700"
                size="lg">
                Закрыть
              </Button>
            </DialogFooter>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={cancelSuccessDialog} onOpenChange={setCancelSuccessDialog}>
        <DialogContent className="rounded-lg overflow-hidden p-0 sm:max-w-[425px]">
          <div className="flex items-center justify-center bg-primary/10 p-6">
            <div className="flex h-16 w-16 items-center justify-center rounded-full bg-primary/20">
              <Check className="h-8 w-8 text-primary" />
            </div>
          </div>
          <div className="p-6">
            <DialogHeader>
              <DialogTitle className="mb-2 text-center text-xl">
                Занятие успешно отменено
              </DialogTitle>
              <DialogDescription className="text-center text-base">
                <div className="space-y-4">
                  <div className="flex items-center justify-center gap-2 text-gray-600">
                    <CalendarDays className="h-5 w-5" />
                    <span>{formatDate(slot.date_time)}</span>
                  </div>
                  <div className="flex items-center justify-center gap-2 text-gray-600">
                    <Building2 className="h-5 w-5" />
                    <span>АкваСтар: {slot.club_title}</span>
                  </div>
                  <div className="flex items-center justify-center gap-2 text-gray-600">
                    <User className="h-5 w-5" />
                    <span>Инструктор: {trainerListActiveState[0]?.name || 'Не указан'}</span>
                  </div>
                  <div className="flex items-center justify-center gap-2 text-gray-600">
                    <UserCircle className="h-5 w-5" />
                    <span>Ребенок: {userName}</span>
                  </div>
                </div>
              </DialogDescription>
            </DialogHeader>
            <DialogFooter className="mt-8">
              <Button onClick={handleCancelSuccessClose} className="w-full" size="lg">
                Готово
              </Button>
            </DialogFooter>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export const SignUpSlotStrSkeleton = () => {
  return (
    <div className={s.wrapper}>
      <div className={s.content}>
        <div className={s.timeBlock}>
          <Skeleton className="h-4 w-4" />
          <Skeleton className="h-4 w-16" />
        </div>
        <div className={s.trainerBlock}>
          <Skeleton className="h-4 w-4" />
          <Skeleton className="h-4 w-32" />
        </div>
      </div>
      <Skeleton className="h-8 w-[90px]" />
    </div>
  );
};

export default SignUpSlot;
